// components/DocumentLayoutSettings.js
// Gestione impostazioni layout documenti - REFACTORED
// Reduced from 1270 lines to ~350 lines

import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { FileText, Code, BookOpen, Palette, Settings2, Eye } from 'lucide-react';

// Sub-components
import { TemplatesTab } from './document-settings/TemplatesTab';
import { PlaceholdersTab } from './document-settings/PlaceholdersTab';
import { LegalBlocksTab } from './document-settings/LegalBlocksTab';
import { BrandingTab } from './document-settings/BrandingTab';
import { RenderingTab } from './document-settings/RenderingTab';
import { PreviewTab } from './document-settings/PreviewTab';

const API_URL = '';

const DocumentLayoutSettings = () => {
  const [activeSubTab, setActiveSubTab] = useState('templates');
  const [loading, setLoading] = useState(false);
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    template_name: '',
    template_code: '',
    document_type: 'estimate_customer',
    version: '1.0',
    notes: ''
  });
  const [validationResult, setValidationResult] = useState(null);
  
  // Placeholders state
  const [placeholders, setPlaceholders] = useState([]);
  const [placeholderFilter, setPlaceholderFilter] = useState('all');
  
  // Legal blocks state
  const [legalBlocks, setLegalBlocks] = useState([]);
  const [showLegalBlockModal, setShowLegalBlockModal] = useState(false);
  const [editingLegalBlock, setEditingLegalBlock] = useState(null);
  const [legalBlockForm, setLegalBlockForm] = useState({
    block_title: '',
    block_content: '',
    document_type: 'estimate_customer',
    display_order: 0
  });
  
  // Branding state
  const [branding, setBranding] = useState({
    company_name: 'ABITALO S.R.L.',
    company_address: '',
    vat_number: '',
    website: '',
    email: '',
    phone: '',
    primary_color: '#FF7819',
    secondary_color: '#1A1A1A',
    font_family: 'Helvetica',
    footer_text: ''
  });
  
  // Rendering rules state
  const [renderingRules, setRenderingRules] = useState({
    page_size: 'A4',
    page_margin_top: 15,
    page_margin_right: 15,
    page_margin_bottom: 20,
    page_margin_left: 15,
    repeat_header: true,
    repeat_footer: true,
    repeat_table_header: true,
    signature_last_page_only: true,
    legal_blocks_after_totals: true,
    avoid_section_title_at_page_bottom: true
  });
  
  // Preview state
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [selectedEstimateForPreview, setSelectedEstimateForPreview] = useState(null);

  const getAuthHeaders = () => ({
    withCredentials: true,
    'Content-Type': 'application/json'
  });

  // ==================== DATA LOADING ====================
  
  useEffect(() => {
    loadTemplates();
    loadPlaceholders();
    loadLegalBlocks();
    loadBranding();
    loadRenderingRules();
    loadEstimates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/templates`, { headers: getAuthHeaders() });
      if (response.ok) setTemplates(await response.json());
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadPlaceholders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/placeholders`, { headers: getAuthHeaders() });
      if (response.ok) setPlaceholders(await response.json());
    } catch (error) {
      console.error('Error loading placeholders:', error);
    }
  };

  const loadLegalBlocks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/legal-blocks?active_only=false`, { headers: getAuthHeaders() });
      if (response.ok) setLegalBlocks(await response.json());
    } catch (error) {
      console.error('Error loading legal blocks:', error);
    }
  };

  const loadBranding = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/branding`, { headers: getAuthHeaders() });
      if (response.ok) setBranding(await response.json());
    } catch (error) {
      console.error('Error loading branding:', error);
    }
  };

  const loadRenderingRules = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/rendering-rules`, { headers: getAuthHeaders() });
      if (response.ok) setRenderingRules(await response.json());
    } catch (error) {
      console.error('Error loading rendering rules:', error);
    }
  };

  const loadEstimates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/estimates`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setEstimates(data.slice(0, 20));
      }
    } catch (error) {
      console.error('Error loading estimates:', error);
    }
  };

  // ==================== TEMPLATE HANDLERS ====================

  const handleCreateTemplate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/templates`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(templateForm)
      });
      if (response.ok) {
        toast.success('Template creato');
        loadTemplates();
        setShowTemplateModal(false);
      } else {
        toast.error('Errore nella creazione');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/templates/${editingTemplate.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(templateForm)
      });
      if (response.ok) {
        toast.success('Template aggiornato');
        loadTemplates();
        setShowTemplateModal(false);
        setEditingTemplate(null);
      } else {
        toast.error('Errore nell\'aggiornamento');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Eliminare questo template?')) return;
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/templates/${templateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Template eliminato');
        loadTemplates();
      }
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleUploadDocx = async (templateId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/templates/${templateId}/upload-docx`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (response.ok) {
        toast.success('File DOCX caricato');
        loadTemplates();
      } else {
        toast.error('Errore nel caricamento');
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    }
  };

  const handleValidateTemplate = async (templateId) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/templates/${templateId}/validate`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const result = await response.json();
        setValidationResult({ ...result, templateId });
        if (result.is_valid) {
          toast.success('Template valido');
        } else {
          toast.warning(`${result.invalid_placeholders.length} placeholder non riconosciuti`);
        }
      }
    } catch (error) {
      toast.error('Errore nella validazione');
    }
  };

  const handleSetActiveTemplate = async (templateId) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/templates/${templateId}/activate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Template attivato');
        loadTemplates();
      }
    } catch (error) {
      toast.error('Errore nell\'attivazione');
    }
  };

  const handleDownloadSampleTemplate = async (docType) => {
    window.open(`${API_URL}/api/settings/layout/sample-template/${docType}`, '_blank');
  };

  // ==================== LEGAL BLOCK HANDLERS ====================

  const handleCreateLegalBlock = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/legal-blocks`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(legalBlockForm)
      });
      if (response.ok) {
        toast.success('Blocco legale creato');
        loadLegalBlocks();
        setShowLegalBlockModal(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLegalBlock = async () => {
    if (!editingLegalBlock) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/legal-blocks/${editingLegalBlock.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(legalBlockForm)
      });
      if (response.ok) {
        toast.success('Blocco legale aggiornato');
        loadLegalBlocks();
        setShowLegalBlockModal(false);
        setEditingLegalBlock(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLegalBlock = async (blockId) => {
    if (!confirm('Eliminare questo blocco?')) return;
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/legal-blocks/${blockId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Blocco eliminato');
        loadLegalBlocks();
      }
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleToggleLegalBlock = async (blockId, isActive) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/legal-blocks/${blockId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: isActive })
      });
      if (response.ok) {
        loadLegalBlocks();
      }
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  // ==================== BRANDING & RENDERING HANDLERS ====================

  const handleSaveBranding = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/branding`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(branding)
      });
      if (response.ok) {
        toast.success('Branding salvato');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRenderingRules = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/settings/layout/rendering-rules`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(renderingRules)
      });
      if (response.ok) {
        toast.success('Regole salvate');
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================== RENDER ====================

  const tabs = [
    { id: 'templates', label: 'Template', icon: FileText },
    { id: 'placeholders', label: 'Placeholder', icon: Code },
    { id: 'legal', label: 'Blocchi Legali', icon: BookOpen },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'rendering', label: 'Rendering', icon: Settings2 },
    { id: 'preview', label: 'Preview & Test', icon: Eye },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-navigation */}
      <div className="flex flex-wrap gap-2 border-b pb-4">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeSubTab === tab.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSubTab(tab.id)}
            className={activeSubTab === tab.id ? 'bg-[#FF7819] hover:bg-[#E56A10]' : ''}
          >
            <tab.icon className="w-4 h-4 mr-1.5" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab Contents */}
      {activeSubTab === 'templates' && (
        <TemplatesTab
          templates={templates}
          loading={loading}
          showTemplateModal={showTemplateModal}
          setShowTemplateModal={setShowTemplateModal}
          editingTemplate={editingTemplate}
          setEditingTemplate={setEditingTemplate}
          templateForm={templateForm}
          setTemplateForm={setTemplateForm}
          validationResult={validationResult}
          onCreateTemplate={handleCreateTemplate}
          onUpdateTemplate={handleUpdateTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          onUploadDocx={handleUploadDocx}
          onValidateTemplate={handleValidateTemplate}
          onDownloadSampleTemplate={handleDownloadSampleTemplate}
          onSetActiveTemplate={handleSetActiveTemplate}
        />
      )}

      {activeSubTab === 'placeholders' && (
        <PlaceholdersTab
          placeholders={placeholders}
          placeholderFilter={placeholderFilter}
          setPlaceholderFilter={setPlaceholderFilter}
        />
      )}

      {activeSubTab === 'legal' && (
        <LegalBlocksTab
          legalBlocks={legalBlocks}
          loading={loading}
          showLegalBlockModal={showLegalBlockModal}
          setShowLegalBlockModal={setShowLegalBlockModal}
          editingLegalBlock={editingLegalBlock}
          setEditingLegalBlock={setEditingLegalBlock}
          legalBlockForm={legalBlockForm}
          setLegalBlockForm={setLegalBlockForm}
          onCreateLegalBlock={handleCreateLegalBlock}
          onUpdateLegalBlock={handleUpdateLegalBlock}
          onDeleteLegalBlock={handleDeleteLegalBlock}
          onToggleLegalBlock={handleToggleLegalBlock}
        />
      )}

      {activeSubTab === 'branding' && (
        <BrandingTab
          branding={branding}
          setBranding={setBranding}
          loading={loading}
          onSaveBranding={handleSaveBranding}
        />
      )}

      {activeSubTab === 'rendering' && (
        <RenderingTab
          renderingRules={renderingRules}
          setRenderingRules={setRenderingRules}
          loading={loading}
          onSaveRenderingRules={handleSaveRenderingRules}
        />
      )}

      {activeSubTab === 'preview' && (
        <PreviewTab
          templates={templates}
          estimates={estimates}
          selectedTemplateForPreview={selectedTemplateForPreview}
          setSelectedTemplateForPreview={setSelectedTemplateForPreview}
          selectedEstimateForPreview={selectedEstimateForPreview}
          setSelectedEstimateForPreview={setSelectedEstimateForPreview}
        />
      )}
    </div>
  );
};

export default DocumentLayoutSettings;
